import Vue from "vue";
import VueRouter from "vue-router";
import RouteObjects from "@/shared/router/route-objects";
import AdvisorStore from "@/advisor/store";
import {
  resolveAccountContext,
  selectionRouteTier,
} from "@/advisor/lib/navigation";

var router = {};

// Account loads in flight. The overlay below is shared and holds no count of its own -- whoever
// hides it last wins -- so two overlapping navigations would have the first lift the block while
// the second is still loading.
var pendingAccountLoads = 0;

if (window.location.host.match(/advisorportal.*\.penchecks\.com/g) !== null) {
  Vue.use(VueRouter);

  const routes = [
    // Sign Up
    {
      path: "/sign-up-nlira/:emailAddress",
      name: "Sign up investment advisor",
      meta: { title: "Sign up investment advisor", allowsAnonymousUser: true },
      component: () => import("../views/users/PcSignUpRia.vue"),
    },
    {
      path: "/account-creation-ria",
      meta: { title: "Account Creation Advisor", needsNoAccount: true },
      component: () => import("../views/users/PcAccountCreationRia.vue"),
    },
    {
      path: "/access-removed",
      name: "Access Removed",
      meta: { title: "Access Removed", needsNoAccount: true },
      component: () => import("@/shared/components/PcAccessRemoved.vue"),
    },
    {
      path: "/invitation-pending",
      name: "Invitation Pending",
      meta: { title: "Invitation Pending", needsNoAccount: true },
      component: () => import("@/shared/components/PcAccessRemoved.vue"),
      props: {
        heading: "Invitation pending",
        description:
          "You don't currently have access to this account. If you're expecting an invitation, check your email for the invite link. Otherwise, contact your firm admin.",
      },
    },
    {
      path: "/user-invitation/:accountUserUuid([a-zA-Z0-9]{32})",
      name: "User Invite",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/users/PcAdvisorUserInvite.vue"),
    },
    {
      path: "/forgot-password",
      name: "Forgot Password",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/users/PcAdvisorForgotPassword.vue"),
    },
    {
      path: "/reset-password/:email_address/:reset_password_token",
      name: "Reset Password",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/users/PcAdvisorResetPassword.vue"),
    },
    // Login
    {
      path: "/",
      meta: { allowsAnonymousUser: true },
      redirect: "/login",
    },
    {
      path: "/login",
      name: "Home",
      meta: { title: "Home", allowsAnonymousUser: true },
      component: () => import("../views/users/PcLogin.vue"),
    },
    // Home Page
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})",
      meta: {
        title: "Dashboard",
        tiers: ["registeredInvestmentAdvisor", "advisoryFirm", "advisor"],
      },
      component: () => import("../views/PcAdvisorPageHome.vue"),
    },
    // User settings - the logged-in user's own settings, not tied to an entity's account
    // One component for both: the step is the same control, and which tier it is choosing comes
    // from the path.
    {
      path: "/select-advisory-firm",
      meta: { title: "Choose an advisory firm" },
      component: () => import("../views/PcAdvisorSelectAccount.vue"),
    },
    {
      path: "/select-advisor",
      meta: { title: "Choose an advisor" },
      component: () => import("../views/PcAdvisorSelectAccount.vue"),
    },
    {
      path: "/user-settings",
      meta: { title: "User settings" },
      component: () => import("../views/PcAdvisorMainUserSettings.vue"),
    },
    // Register Investment Advisor
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/ria-reports",
      meta: { title: "Reports", tiers: ["registeredInvestmentAdvisor"] },
      component: () => import("../views/ria/PcRiaMainReports.vue"),
    },
    // These two routes are tier-shared: both the RIA and its advisory firms read the RIA's
    // data, via their own account uuid in the path. A tier match no longer implies the route
    // account is reachable -- the store's account-load / holdsTier checks (see
    // ensureCurrentAccount in store/index.js) and the backend middleware refuse an
    // above-anchor account.
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/user-agreements",
      meta: {
        title: "User agreements",
        tiers: ["registeredInvestmentAdvisor", "advisoryFirm"],
      },
      component: () =>
        import("../views/next-level/PcNextLevelMainUserAgreements.vue"),
    },
    // Predates the tier-neutral rename (TECH-12602) -- kept in case bookmarked or linked.
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/ria-user-agreements",
      redirect: (to) => `/accounts/${to.params.accountUuid}/user-agreements`,
    },
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/advisor-email-templates",
      meta: {
        title: "Advisor email templates",
        tiers: ["registeredInvestmentAdvisor", "advisoryFirm"],
      },
      component: () =>
        import("../views/next-level/PcNextLevelMainAdvisorEmailTemplates.vue"),
    },
    // Predates the tier-neutral rename (TECH-12602) -- kept in case bookmarked or linked.
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/ria-email-templates",
      redirect: (to) =>
        `/accounts/${to.params.accountUuid}/advisor-email-templates`,
    },
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/ria-fee-schedules",
      meta: {
        title: "RIA fee schedules",
        tiers: ["registeredInvestmentAdvisor"],
      },
      component: () => import("../views/ria/PcRiaFeeSchedule.vue"),
    },
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/ria-account",
      meta: {
        title: "Account details",
        tiers: ["registeredInvestmentAdvisor"],
      },
      component: () => import("../views/ria/PcRiaMainAccount.vue"),
    },
    {
      path: "/advisory-firms/:accountUuid([a-zA-Z0-9]{32})",
      meta: { title: "Ria Firms", tiers: ["registeredInvestmentAdvisor"] },
      component: () =>
        import("../views/ria/advisory-firm/PcRiaAdvisoryFirm.vue"),
    },
    {
      path: "/advisors/:accountUuid([a-zA-Z0-9]{32})",
      meta: { title: "Advisor", tiers: ["registeredInvestmentAdvisor"] },
      component: () =>
        import(
          "../views/ria/advisory-firm/advisor/PcRiaAdvisoryFirmAdvisor.vue"
        ),
    },
    {
      path: "/ria-advisor-campaigns/:campaignUuid([a-zA-Z0-9]{32})",
      meta: { title: "Campaign" },
      component: () =>
        import(
          "../views/ria/advisory-firm/advisor/campaign/PcRiaAdvisorCampaign.vue"
        ),
    },
    {
      path: "/ria-advisor-campaign-prospects/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Prospect" },
      component: () =>
        import(
          "../views/ria/advisory-firm/advisor/campaign/prospect/PcRiaAdvisorCampaignProspect.vue"
        ),
    },
    {
      path: "/ria-advisor-prospects/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Prospect" },
      component: () =>
        import(
          "../views/ria/advisory-firm/advisor/prospect/PcRiaAdvisorProspect.vue"
        ),
    },
    {
      path: "/ria-advisor-account-holders/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Account Holder" },
      component: () =>
        import(
          "../views/ria/advisory-firm/advisor/account-holder/PcRiaAdvisorAccountHolder.vue"
        ),
    },
    // Advisory Firms
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/advisory-firm-reports",
      meta: { title: "Reports", tiers: ["advisoryFirm"] },
      component: () =>
        import("../views/advisory-firm/PcAdvisoryFirmMainReports.vue"),
    },
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/advisory-firm-account",
      meta: { title: "Account details", tiers: ["advisoryFirm"] },
      component: () =>
        import("../views/advisory-firm/PcAdvisoryFirmMainAccount.vue"),
    },
    {
      path: "/firm-advisors/:accountUuid([a-zA-Z0-9]{32})",
      meta: { title: "Advisor", tiers: ["advisoryFirm"] },
      component: () =>
        import("../views/advisory-firm/advisor/PcAdvisoryFirmAdvisor.vue"),
    },
    {
      path: "/firm-advisor-campaigns/:campaignUuid([a-zA-Z0-9]{32})",
      meta: { title: "Campaign" },
      component: () =>
        import(
          "../views/advisory-firm/advisor/campaign/PcAdvisoryFirmAdvisorCampaign.vue"
        ),
    },
    {
      path: "/firm-advisor-campaign-prospects/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Prospect" },
      component: () =>
        import(
          "../views/advisory-firm/advisor/campaign/prospect/PcAdvisoryFirmAdvisorCampaignProspect.vue"
        ),
    },
    {
      path: "/firm-advisor-prospects/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Prospect" },
      component: () =>
        import(
          "../views/advisory-firm/advisor/prospect/PcAdvisoryFirmAdvisorProspect.vue"
        ),
    },
    {
      path: "/firm-advisor-account-holders/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Account Holder" },
      component: () =>
        import(
          "../views/advisory-firm/advisor/account-holder/PcAdvisoryFirmAdvisorAccountHolder.vue"
        ),
    },
    // Advisors
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/advisor-reports",
      meta: { title: "Reports", tiers: ["advisor"] },
      component: () => import("../views/advisor/PcAdvisorMainReports.vue"),
    },
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/advisor-account",
      meta: { title: "Account details", tiers: ["advisor"] },
      component: () => import("../views/advisor/PcAdvisorMainAccount.vue"),
    },
    {
      path: "/accounts/:accountUuid([a-zA-Z0-9]{32})/account-holders",
      meta: { title: "Advisor Account Holders", tiers: ["advisor"] },
      component: () => import("../views/advisor/PcAdvisorAccountHolder.vue"),
    },
    {
      path: "/campaigns/:campaignUuid([a-zA-Z0-9]{32})",
      meta: { title: "Campaign Prospects", tiers: ["advisor"] },
      component: () =>
        import("../views/advisor/campaign/PcAdvisorCampaign.vue"),
    },
    {
      path: "/campaign-prospects/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Prospect", tiers: ["advisor"] },
      component: () =>
        import(
          "../views/advisor/campaign/prospect/PcAdvisorCampaignProspect.vue"
        ),
    },
    {
      path: "/prospects/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Prospect", tiers: ["advisor"] },
      component: () =>
        import("../views/advisor/prospect/PcAdvisorProspect.vue"),
    },
    {
      path: "/account-holders/:prospectUuid([a-zA-Z0-9]{32})",
      meta: { title: "Account Holder", tiers: ["advisor"] },
      component: () =>
        import(
          "../views/advisor/account-holder/PcAdvisorAccountHolderDetails.vue"
        ),
    },
  ];

  router = new VueRouter({
    mode: "history",
    routes,
  });

  // Registered for the route-change notification it sends to $urlHash, which the shared tab
  // components rely on -- not for the objects. This portal reads none of them, and the account it
  // would load on every /accounts/:accountUuid navigation is the one the guard already loaded.
  router.objects = new RouteObjects(router, { buildsObjects: false });

  router.beforeEach(async (to, from, next) => {
    const permissionsHydrated = (u) =>
      !!u &&
      (Array.isArray(u.clientPermissions) ||
        Array.isArray(u.staffMemberPermissions) ||
        Array.isArray(u.nationalRegistryClientPermissions));

    async function hydrateIfNeeded() {
      // If no user, hydrate
      if (!AdvisorStore.state.authenticatedUser) {
        try {
          await AdvisorStore.dispatch("refreshAuthenticatedUser");
        } catch (exception) {
          // Prevents routing from being blocked on auth refresh errors
          const status = exception?.response?.status;
          const code = exception?.response?.data?.code || exception?.code;

          // Common "expected" cases: not authenticated/expired, forbidden, etc.
          const expected =
            status === 401 ||
            status === 403 ||
            code === "TOKEN_EXPIRED" ||
            code === "NOT_AUTHENTICATED";

          if (!expected) {
            console.warn("[auth] refreshAuthenticatedUser failed", {
              status,
              code,
              message: exception?.message,
              route: to?.fullPath,
            });
          }
        }
        return;
      }
      // If user exists but perms are missing, hydrate
      if (!permissionsHydrated(AdvisorStore.state.authenticatedUser)) {
        try {
          await AdvisorStore.dispatch("refreshAuthenticatedUser");
        } catch (exception) {
          // Prevents routing from being blocked on auth refresh errors
          const status = exception?.response?.status;
          const code = exception?.response?.data?.code || exception?.code;

          // Common "expected" cases: not authenticated/expired, forbidden, etc.
          const expected =
            status === 401 ||
            status === 403 ||
            code === "TOKEN_EXPIRED" ||
            code === "NOT_AUTHENTICATED";

          if (!expected) {
            console.warn("[auth] refreshAuthenticatedUser failed", {
              status,
              code,
              message: exception?.message,
              route: to?.fullPath,
            });
          }
        }
      }
    }

    function isAnonymousRoute() {
      return to.matched.some(
        (route) => route.meta && route.meta.allowsAnonymousUser
      );
    }

    // Deliberately account-less routes - loading one would 403 and block the route before it renders.
    function needsNoAccountRoute() {
      return to.matched.some(
        (route) => route.meta && route.meta.needsNoAccount
      );
    }

    // Always hydrate before resolving (for protected routes)
    if (!isAnonymousRoute()) {
      await hydrateIfNeeded();
    }

    // Where a cold landing starts. Only when nothing is in context yet: a route that names no
    // account needs one to exist, but never moves it.
    function landingAccountUuid() {
      if (AdvisorStore.state.masterAccount) return null;
      if (needsNoAccountRoute()) return null;

      const anchorAccount = AdvisorStore.getters.anchorAccount;
      const authenticatedUser = AdvisorStore.state.authenticatedUser;

      return (
        (anchorAccount && anchorAccount.uuid) ||
        (authenticatedUser && authenticatedUser.lastAccessedAccountId) ||
        null
      );
    }

    // Resolved before resolveNextRoute so that function stays synchronous: it is the single exit
    // point and every branch of it must call next() exactly once.
    let accountContext = { allowed: true };
    let skipSelectionTo = null;
    if (!isAnonymousRoute() && AdvisorStore.state.authenticatedUser) {
      accountContext = resolveAccountContext(
        to,
        AdvisorStore.getters.anchorAccount
      );

      const selectionTier = selectionRouteTier(to.path);

      // Awaited so the page never renders against the account being left. It returns without
      // asking when the account has not changed, which is every navigation within one account.
      if (accountContext.allowed) {
        try {
          // Input is blocked while the account loads, which fixes a real defect rather than
          // decorating one. While this navigation awaits, the address bar still names the account
          // being left, so a second click on that account's own tab targets the current route --
          // a duplicate, which vue-router runs no guard for but which overwrites the pending
          // route, silently cancelling this navigation after its account has been committed.
          //
          // blur() because the overlay swallows pointer events but not keystrokes, and the tab
          // just clicked would still answer Enter. The browser's Back button it cannot block at
          // all: ensureCurrentAccount covers that, and neither half is complete alone.
          //
          // Everything above this line is synchronous for an already-hydrated user. Should
          // hydrateIfNeeded ever wait on the network here, this has to move above it.
          pendingAccountLoads += 1;
          document.activeElement && document.activeElement.blur();
          window.$overlay.show({ text: "Loading account..." });

          await AdvisorStore.dispatch("ensureCurrentAccount", {
            accountUuid:
              accountContext.contextAccountUuid || landingAccountUuid(),
            path: to.fullPath,
          });

          // Whether the step that asks which account to operate as has anything to ask. Answered
          // here because the step itself can only answer it once it exists: it would have to
          // fetch the list, so the card is on screen by the time the answer arrives, and leaves
          // again. Under the same block, so the wait is one span of the overlay rather than two.
          //
          // A tier is only reached this way the first time -- afterwards the tab remembers the
          // account and points straight at it -- but a bookmarked link arrives here every time.
          if (selectionTier) {
            await AdvisorStore.dispatch("ensureReachableAccounts");

            // A tier that could not be fetched is not a tier of one: the search caches nothing on
            // failure, so the list stays absent, the step is shown, and the next navigation
            // through it is the retry.
            const reachable =
              AdvisorStore.state.reachableAccountsByTier[selectionTier] || [];

            if (reachable.length === 1) {
              skipSelectionTo = `/accounts/${reachable[0].id}`;
            }
          }
        } finally {
          pendingAccountLoads -= 1;
          if (pendingAccountLoads === 0) window.$overlay.hide();
        }
      }
    }

    function resolveNextRoute() {
      console.log("to path", to.path);
      if (to.matched.some((record) => record.meta.allowsAnonymousUser)) {
        if (AdvisorStore.state.authenticatedUser) {
          var accountId =
            AdvisorStore.state.authenticatedUser.lastAccessedAccountId;
          if (
            to.path != `/accounts/${accountId}` &&
            AdvisorStore.state.authenticatedUser.lastPasswordChangeAt !==
              null &&
            to.path != "/confirm-email-address"
          ) {
            return next(`/accounts/${accountId}`);
          }
        }

        return next();
      } else {
        if (!AdvisorStore.state.authenticatedUser) {
          if (to.path != "/") {
            return next("/");
          }

          return next();
        }

        // The navigation is allowed through so the address bar keeps the link that failed: it is
        // usually stale, pasted, or somebody else's.
        if (!accountContext.allowed) {
          AdvisorStore.commit("setBlockedRoute", {
            path: to.fullPath,
            reason: "denied",
          });
          return next();
        }

        return next();
      }
    }

    if (!isAnonymousRoute() && !AdvisorStore.state.authenticatedUser) {
      try {
        await AdvisorStore.dispatch("refreshAuthenticatedUser");
      } catch (exception) {
        // Prevents routing from being blocked on auth refresh errors
      }
    }

    if (skipSelectionTo) return next(skipSelectionTo);

    // wait for authenticated user to finish loading before resolving route
    return resolveNextRoute();
  });

  router.afterEach((to) => {
    if (to.meta && to.meta.title) {
      document.title = to.meta.title + " | PenChecks Advisor";
    } else {
      document.title = "PenChecks Advisor";
    }

    if (router.app && router.app.$gtag) {
      router.app.$gtag.pageview({
        page_title: document.title,
        page_path: to.path,
        page_location: window.location.href,
      });
    }
  });
}

export default router;
